// Production uses the same full-stack Worker for the SPA and `/api` routes.
// Vite proxies `/api` to the local backend during development. Override the
// base only when deliberately running the API on a different local origin.
const BASE = import.meta.env.VITE_API_URL
  ? String(import.meta.env.VITE_API_URL).replace(/\/$/, "")
  : "/api";

import type {
  AcceptanceCard,
  ApplicationReceipt,
  NewApplication,
  Note,
  NewNote,
  NewWallAsk,
  WallAsk,
  WallAskRejection,
} from "@autter-same-boat/shared";
export type { ApplicationReceipt, NewApplication, Note, NewNote };
export type { AcceptanceCard, NewWallAsk, WallAsk, WallAskRejection };

// Carries the HTTP status and parsed body so callers can tell a moderation
// rejection (422) apart from an outage (5xx). Extends Error, so existing
// generic catch sites keep working unchanged.
export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
  });
  if (!res.ok) {
    const raw = await res.text().catch(() => "");
    let body: unknown;
    try {
      body = raw ? JSON.parse(raw) : undefined;
    } catch {
      body = raw;
    }
    throw new ApiError(res.status, `${res.status} ${raw || res.statusText}`, body);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  health: () => req<{ ok: boolean; env: string; time: number }>("/health"),
  listNotes: () => req<Note[]>("/notes"),
  createNote: (body: NewNote) =>
    req<Note>("/notes", { method: "POST", body: JSON.stringify(body) }),
  deleteNote: (id: string) => req<void>(`/notes/${id}`, { method: "DELETE" }),
  createApplication: (body: NewApplication) =>
    req<ApplicationReceipt>("/applications", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  getAcceptance: (token: string) =>
    req<AcceptanceCard>(`/acceptances/${encodeURIComponent(token)}`),
  listWallAsks: () => req<WallAsk[]>("/wall-asks"),
  createWallAsk: (body: NewWallAsk) =>
    req<WallAsk>("/wall-asks", { method: "POST", body: JSON.stringify(body) }),
};
